import { ASSET_STATUS } from '../constants/assetStatus';

/**
 * Validation utilities for sub-location operations
 * These utilities ensure data integrity for sub-location save and activate operations
 */

/**
 * Checks if the specified area tax location is inactive
 * @param {string|number} selectedAreaTaxId - The ID of the area tax location to check
 * @param {Object} subLocationDetails - The sub-location details object, possibly containing areaTaxLocations
 * @param {Array} areaTaxLocationsList - List of available area tax locations
 * @returns {boolean} - True if the area tax location is inactive, false otherwise
 */
const areaTaxLocationIsInactive = (selectedAreaTaxId, subLocationDetails, areaTaxLocationsList = []) => {
  if (!selectedAreaTaxId) {
    return false;
  }

  if (
    selectedAreaTaxId === subLocationDetails?.areaTaxLocations?.id &&
    subLocationDetails?.areaTaxLocations?.status !== undefined
  ) {
    return subLocationDetails.areaTaxLocations.status === false;
  }

  const selectedAreaTax = areaTaxLocationsList?.find((loc) => loc.id === selectedAreaTaxId);
  return selectedAreaTax?.status === false;
};

export const validateSubLocationActivation = (subLocationDetails) => {
  if (!subLocationDetails) {
    console.warn('subLocationDetails is required for activation validation');
    return { isValid: false, hasActiveAssets: false, hasInactiveAreaTax: false };
  }

  let hasActiveAssets = false;
  let hasInactiveAreaTax = false;

  // Check for active assets
  const assets = Array.isArray(subLocationDetails?.assets) ? subLocationDetails.assets : [];
  const activeAsset = assets.find((asset) => asset.statusId === ASSET_STATUS.ACTIVE);
  if (activeAsset) {
    hasActiveAssets = true;
  }

  // Check for inactive area tax location (backend now includes status)
  if (subLocationDetails?.areaTaxLocations?.status === false) {
    hasInactiveAreaTax = true;
  }

  return {
    isValid: !hasActiveAssets && !hasInactiveAreaTax,
    hasActiveAssets,
    hasInactiveAreaTax,
  };
};

/**
 * Checks if the currently selected area tax location in form is inactive
 * @param {Object} currentValues - Current form values
 * @param {Object} subLocationDetails - Original sub-location details with backend status
 * @param {Array} areaTaxLocationsList - List of available area tax locations
 * @returns {boolean} - True if selected area tax location is inactive
 */
export const isSelectedAreaTaxLocationInactive = (currentValues, subLocationDetails, areaTaxLocationsList = []) => {
  const selectedAreaTaxId = currentValues?.areaTaxLocation?.id || currentValues?.areaTaxLocationId;
  return areaTaxLocationIsInactive(selectedAreaTaxId, subLocationDetails, areaTaxLocationsList);
};

/**
 * Validates if a sub-location can be saved with the current area tax location selection
 * @param {Object} formData - The form data being submitted
 * @param {Object} subLocationDetails - The original sub-location details with backend status
 * @param {Array} areaTaxLocationsList - List of available area tax locations
 * @returns {Object} - { isValid: boolean, hasInactiveAreaTax: boolean }
 */
export const validateSubLocationSave = (formData, subLocationDetails, areaTaxLocationsList = []) => {
  if (!formData || !subLocationDetails) {
    console.warn('formData and subLocationDetails are required for save validation');
    return { isValid: false, hasInactiveAreaTax: false };
  }

  const selectedAreaTaxId = formData?.areaTaxLocationId || formData?.areaTaxLocation?.id;
  const hasInactiveAreaTax = areaTaxLocationIsInactive(selectedAreaTaxId, subLocationDetails, areaTaxLocationsList);

  return {
    isValid: !hasInactiveAreaTax,
    hasInactiveAreaTax,
  };
};
